import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import Button from '../../../shared/components/ui/Button';
import Modal from '../../../shared/components/ui/Modal';
import FormInput from '../../../shared/components/ui/FormInput';
import Play from '../../../shared/assets/svg/play.svg';
import { addCompanySecret } from '../../services/api/implementation/impl';
import { COMPANY_SMTP_STATUSES_QUERY_KEY } from '../../utils/emailSendersAccess';
import { EMAIL_LEAD_REUSABLE_SENDERS_QUERY_KEY } from '../../utils/emailLeadsAccess';
import AppPasswordHelp from './AppPasswordHelp';

/**
 * One SMTP sender per company — what used to be Integrations → Email Setup →
 * View Companies. Same cards, same Configure form, same endpoint; it now sits
 * on the Outbound tab next to the inboxes that use the same app password.
 */

function companyLogoUrl(company) {
  if (Array.isArray(company.company_logo)) return company.company_logo[0]?.url ?? null;
  return company.company_logo?.url || company.company_logo || null;
}

export default function CompanySenders({ companySenders, isPending }) {
  const queryClient = useQueryClient();
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm();

  const { mutate: saveCompanySecret, isPending: isSubmitting } = useMutation({
    mutationFn: (payload) => addCompanySecret(payload),
    onSuccess: () => {
      toast.success('Email settings configured successfully');
      setSelectedCompanyId(null);
      reset();
      queryClient.invalidateQueries({ queryKey: [COMPANY_SMTP_STATUSES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: EMAIL_LEAD_REUSABLE_SENDERS_QUERY_KEY });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Error configuring email');
    }
  });

  const openConfigure = (companyId) => {
    setSelectedCompanyId(companyId);
    reset({
      smtp_host: 'smtp.gmail.com',
      smtp_port: 465,
      smtp_secure: true,
      smtp_user: '',
      smtp_password: '',
      smtp_from_email: '',
      smtp_from_name: ''
    });
  };

  const onSubmit = (data) => {
    saveCompanySecret({
      ...data,
      company_id: selectedCompanyId,
      smtp_host: 'smtp.gmail.com',
      smtp_port: 465,
      smtp_secure: true
    });
  };

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-base font-semibold text-gray-900">Company senders</h3>
        <p className="text-sm text-gray-600">
          Campaigns and notifications for each company go out from its own address.
        </p>
      </div>

      {isPending && companySenders.length === 0 ? (
        <p className="rounded-xl bg-white px-6 py-8 text-center text-sm text-gray-500">
          Loading companies…
        </p>
      ) : companySenders.length === 0 ? (
        <p className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-8 text-center text-sm text-gray-500">
          No companies yet. Add a company first, then connect its email here.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {companySenders.map(({ company, connected, address }) => {
            const logoUrl = companyLogoUrl(company);
            return (
              <div
                key={company.id}
                className="flex h-full flex-col rounded-lg bg-white p-6 shadow-md">
                {logoUrl ? (
                  <img
                    className="mb-4 h-[6rem] border object-contain"
                    src={logoUrl}
                    alt={`${company.company_name} logo`}
                  />
                ) : null}
                <div className="mb-2 flex items-start justify-between gap-2">
                  <h4 className="text-lg font-bold">{company.company_name}</h4>
                  {connected ? (
                    <span className="flex items-center gap-1 text-sm font-medium text-[#418b7a]">
                      <span className="h-2 w-2 rounded-full bg-[#418b7a]" />
                      Live
                    </span>
                  ) : (
                    <span className="text-sm font-medium text-amber-700">Not connected</span>
                  )}
                </div>
                <p className="mb-4 flex-grow text-sm text-gray-600">
                  {connected
                    ? address
                      ? `Sending as ${address}.`
                      : 'Sending is set up for this company.'
                    : 'Connect a Gmail address so campaigns and notifications for this company can be sent.'}
                </p>
                <div className="mt-auto flex w-full justify-between gap-4">
                  <Button displayType="secondary" onClick={() => setIsDemoModalOpen(true)}>
                    <span className="flex items-center justify-start gap-1">
                      <img src={Play} alt="" className="pt-1" />
                      Demo
                    </span>
                  </Button>
                  <Button onClick={() => openConfigure(company.id)} disabled={connected}>
                    {connected ? 'Connected' : 'Configure'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedCompanyId ? (
        <Modal
          name="Configure Email"
          onClose={() => !isSubmitting && setSelectedCompanyId(null)}
          width={40}
          isDirty={true}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <AppPasswordHelp />
            <div className="grid grid-cols-1 gap-4">
              <FormInput
                label="SMTP User (Gmail)"
                register_key="smtp_user"
                register={register}
                errors={errors.smtp_user}
                isRequired={true}
                readOnly={isSubmitting}
                width={100}
              />
              <FormInput
                label="SMTP Password (App Password)"
                register_key="smtp_password"
                type="password"
                register={register}
                errors={errors.smtp_password}
                isRequired={true}
                readOnly={isSubmitting}
                width={100}
              />
              <FormInput
                label="From Email"
                register_key="smtp_from_email"
                register={register}
                errors={errors.smtp_from_email}
                isRequired={true}
                readOnly={isSubmitting}
                width={100}
              />
              <FormInput
                label="From Name"
                register_key="smtp_from_name"
                register={register}
                errors={errors.smtp_from_name}
                isRequired={true}
                readOnly={isSubmitting}
                width={100}
              />
            </div>
            <div className="mt-6 flex justify-end gap-4">
              <Button
                type="button"
                displayType="secondary"
                disabled={isSubmitting}
                onClick={() => setSelectedCompanyId(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Connecting...' : 'Connect'}
              </Button>
            </div>
          </form>
        </Modal>
      ) : null}

      {isDemoModalOpen ? (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name="Mail Integration Demo"
          onClose={() => setIsDemoModalOpen(false)}
          width={50}>
          <iframe
            width="100%"
            height="315"
            src="https://www.youtube.com/embed/zv0jXPmKBrk"
            title="Mail Integration Demo"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen></iframe>
        </Modal>
      ) : null}
    </section>
  );
}
